"use strict";

/* eslint-disable default-param-last */
var isEmpty = require('lodash.isempty');
var trim = require('lodash.trim');

/**
 * Returns error if field is blank (value === '')
 * @param {STRING} options.key - Custom format for key name used in custom error message
 * @param {STRING} options.message - custom message for error
 */
var required = function required() {
  var options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var value = arguments.length > 1 ? arguments[1] : undefined;
  var key = arguments.length > 2 ? arguments[2] : undefined;
  // numbers are ok
  var isNumber = typeof value === 'number';

  // strings with only spaces are NOT ok
  var isEmptyString = typeof value === 'string' && isEmpty(trim(value));

  // cannot be empty objects, strings or arrays
  var isEmptyStructure = !isNumber && isEmpty(value);
  var message;
  if (isEmptyString || isEmptyStructure) {
    message = options.message || "A valid ".concat(options.key || key, " is required");
  }
  return message;
};

/**
 * Returns error if value is not a number.  Empty value passes.
 * @param {STRING} options.key - Custom format for key name used in custom error message
 * @param {STRING} options.message - custom message for error
 */
var isNumber = function isNumber() {
  var options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var value = arguments.length > 1 ? arguments[1] : undefined;
  var key = arguments.length > 2 ? arguments[2] : undefined;
  var condition = /^(\d{1,3}(,\d{3})*|(\d*))(\.\d{1,2})?$/;
  var message;
  if (!condition.test(value)) {
    message = options.message || "Please enter a valid ".concat(options.key || key);
  }
  return message;
};

/**
 * Returns error if value is not a dollar amount.  Empty value passes.
 * @param {STRING} options.key - Custom format for key name used in custom error message
 * @param {STRING} options.message - custom message for error
 */
var isDollarValue = function isDollarValue() {
  var options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var value = arguments.length > 1 ? arguments[1] : undefined;
  var key = arguments.length > 2 ? arguments[2] : undefined;
  var condition = /^\$?(\d{1,3}(,\d{3})*|(\d*))(\.\d{1,2})?$/;
  var message;
  if (!condition.test(value)) {
    message = options.message || "Please enter a valid ".concat(options.key || key);
  }
  return message;
};

/**
 * Return errors if value is not within given range.
 * @param  {NUMBER}  options.min
 * @param  {NUMBER}  options.max
 * @param  {STRING}  options.message - custom message for error
 * @param  {STRING}  options.key - Custom format for key name used in custom error message
 * @param  {STRING}  options.underMinMessage - custom message for options.min
 * @param  {STRING}  options.overMaxMessage - custom message for options.max
 */
var range = function range() {
  var options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var value = arguments.length > 1 ? arguments[1] : undefined;
  var k = arguments.length > 2 ? arguments[2] : undefined;
  var message;
  var val = trim(value);
  val = val.replace(/,/g, ''); // _.trim will convert a Number to a String

  if (val !== '') {
    var key = options.key || k;
    var comparator = Number(val);

    /* eslint no-restricted-globals: "error" */
    var failsForMin = options.min !== undefined && (comparator < options.min || isNaN(comparator));
    var failsForMax = options.max !== undefined && (comparator > options.max || isNaN(comparator));
    /* eslint no-restricted-globals: "error" */
    var defaultErrorMessage;
    if (options.min && options.max) {
      defaultErrorMessage = options.message || "".concat(key, " must be between ").concat(options.min, " and ").concat(options.max);
    } else if (options.min) {
      defaultErrorMessage = options.message || "".concat(key, " must be ").concat(options.min, " or more");
    } else {
      defaultErrorMessage = options.message || "".concat(key, " must be ").concat(options.max, " or less");
    }
    if (failsForMax && failsForMin) {
      message = options.invalidMessage || defaultErrorMessage;
    } else if (failsForMax) {
      message = options.overMaxMessage || defaultErrorMessage;
    } else if (failsForMin) {
      message = options.underMinMessage || defaultErrorMessage;
    }
  }
  return message;
};

/**
 * Return errors if dollar amount value is not within a certain range. Allows input prepended with $ sign to pass.
 * @param  {NUMBER}  options.min
 * @param  {NUMBER}  options.max
 * @param  {STRING}  options.message  - custom message for error
 * @param  {STRING}  options.key - Custom format for key name used in custom error message
 * @param  {STRING}  options.underMinMessage - custom message for options.min
 * @param  {STRING}  options.overMaxMessage - custom message for options.max
 */

var dollarAmountRange = function dollarAmountRange() {
  var options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var val = arguments.length > 1 ? arguments[1] : undefined;
  var key = arguments.length > 2 ? arguments[2] : undefined;
  var value = trim(val);
  if (value.charAt(0) === '$' && value.length > 1) {
    value = value.substring(1);
  }
  return range(options, value, key);
};

/**
 * Return errors if length of value is not within given range.
 * @param  {NUMBER}  options.min
 * @param  {NUMBER}  options.max
 * @param  {STRING}  options.message - custom message for error
 * @param  {STRING}  options.key - Custom format for key name used in custom error message
 * @param  {STRING}  options.underMinMessage - custom message for options.min
 * @param  {STRING}  options.overMaxMessage - custom message for options.max
 */
var inputLength = function inputLength() {
  var options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var value = arguments.length > 1 ? arguments[1] : undefined;
  var k = arguments.length > 2 ? arguments[2] : undefined;
  var nullOrUndefined = value === 'undefined' || value === 'null' || value === null || value === undefined;
  var comparator = trim("".concat(value)).length;
  var message;
  var key = options.key || k;
  if (comparator !== 0) {
    var failsForMin = options.min !== undefined && (comparator < options.min || nullOrUndefined);
    var failsForMax = options.max !== undefined && (comparator > options.max || nullOrUndefined);
    var defaultErrorMessage;
    if (options.min && options.max) {
      defaultErrorMessage = options.message || "".concat(key, " must be between ").concat(options.min, " and ").concat(options.max, " characters");
    } else if (options.min) {
      defaultErrorMessage = options.message || "".concat(key, " must be ").concat(options.min, " characters or more");
    } else {
      defaultErrorMessage = options.message || "".concat(key, " must be ").concat(options.max, " characters or less");
    }
    if (failsForMax) {
      message = options.overMaxMessage || defaultErrorMessage;
    }
    if (failsForMin) {
      message = options.underMinMessage || defaultErrorMessage;
    }
  }
  return message;
};

/**
 * Returns error if value matches regular expression.
 * @param {REGEX} options.condition - regex condition to test value by
 * @param {STRING} options.message - custom message for error
 * @param { STRING} options.key - Custom format for key name used in custom error message
 */
var customCondition = function customCondition() {
  var options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var value = arguments.length > 1 ? arguments[1] : undefined;
  var key = arguments.length > 2 ? arguments[2] : undefined;
  var message;
  var condition = options.condition;
  if (!(condition instanceof RegExp)) {
    condition = new RegExp(options.condition);
  }
  if (value && condition.test(value)) {
    message = options.message || "A valid ".concat(options.key || key, " is required");
  }
  return message;
};

/**
 * Returns error if value contains '<|>|--|=' or starts with '&' and ends with ';'
 * @param  {STRING} options.message - custom message for error
 */
var suspicious = function suspicious() {
  var options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var value = arguments.length > 1 ? arguments[1] : undefined;
  var conditionOne = /<|>|--|=/;
  var conditionTwo = /^&.*;$/;
  var message;
  if (value && (conditionOne.test(value) || conditionTwo.test(value))) {
    message = options.message || 'You may not have one or more of the following characters: <, >, =, -- or contain strings that start with  "&" and end with ";"';
  }
  return message;
};

/**
 * Returns error if invalid email address
 * @param  {STRING} options.message - custom message for error
 */
var email = function email() {
  var options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var val = arguments.length > 1 ? arguments[1] : undefined;
  var message;
  var value = trim(val);
  var condition = /^[a-zA-Z0-9_+&*-]+(?:\.[a-zA-Z0-9_+&*-]+)*@(?:[a-zA-Z0-9-]+\.)+[a-zA-Z]{2,15}$/;
  // see https://owasp.org/www-community/OWASP_Validation_Regex_Repository

  if (value && !condition.test(value)) {
    message = options.message || 'Please specify a valid email address';
  }
  return message;
};

/**
 * Returns error if input contains sensitive info: ssn# or email address or an asterisk -'*'.
 * @param  {STRING} options.message - custom message for error
 */
var sensitiveInfo = function sensitiveInfo() {
  var options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var value = arguments.length > 1 ? arguments[1] : undefined;
  var ssnCondition = /.*(\d[*.()\s-]*){9,}.*/;
  var emailCondition = /.*(@|(\.com|\.net|\.edu|\.gov|\.org|\.biz)(?![a-z]))./;
  var asteriskCondition = /.*\*.*/;
  var message;
  if (value && (ssnCondition.test(value) || emailCondition.test(value) || asteriskCondition.test(value))) {
    message = options.message || "Please revise your bank account holder's first name to ensure that it doesn't include your name, Social Security Number, email address, or other information that could identify where you live. Please do not use any of the following characters: *";
  }
  return message;
};

/**
 * Returns an error message if the value and options.target value does not equal.
 * @param {STRING} options.target - name attribute of field to compare value to
 * @param {STRING} options.message  - custom message for error
 * @param {STRING} options.key - Custom format for key name used in custom error message
 */
var sameAsField = function sameAsField() {
  var options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var value = arguments.length > 1 ? arguments[1] : undefined;
  var key = arguments.length > 2 ? arguments[2] : undefined;
  var attributes = arguments.length > 3 ? arguments[3] : undefined;
  var equals = true; // if options.target is not set the validation passes.
  var message;
  if (options.target) {
    equals = attributes[options.target] === value;
  }
  if (value && !equals) {
    message = options.message || "".concat(options.key || key, " must match ").concat(options.target);
  }
  return message;
};

/**
 * Returns an error message if the value and options.target value equals.
 * @param {STRING} options.target - name attribute of field to compare value to
 * @param {STRING} options.message - custom message for error
 * @param {STRING} options.key - Custom format for key name used in custom error message
 */
var notSameAsField = function notSameAsField() {
  var options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var value = arguments.length > 1 ? arguments[1] : undefined;
  var key = arguments.length > 2 ? arguments[2] : undefined;
  var attributes = arguments.length > 3 ? arguments[3] : undefined;
  var message;
  var equals = false; // if options.target is not set the validation passes.

  if (options.target) {
    equals = attributes[options.target] === value;
  }
  if (value && equals) {
    message = options.message || "".concat(options.key || key, " must not match ").concat(options.target);
  }
  return message;
};

/**
 * Default condition return error if password does not have 8 or more characters, with at least 1 number and 1 letter
 * Empty value will fail the validation.
 * @param  {STRING} options.condition - condition which value is tested against
 * @param  {STRING} options.message - custom message for error
 */
var validPassword = function validPassword() {
  var options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var value = arguments.length > 1 ? arguments[1] : undefined;
  var condition = new RegExp(options.condition || '(?=.*\\d)(?=.*[A-Z,a-z]).{8,}');
  var message;
  if (value && !condition.test(value)) {
    message = options.message || 'Password must have 8 or more characters, with at least 1 number and 1 letter';
  }
  return message;
};

/**
 * In use to check for two names used in name field. Condition tests for presence of 'and' or '&' surrounded by spaces
 * @param  {STRING} options.message - custom message for error
 */
var twoNames = function twoNames() {
  var options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var value = arguments.length > 1 ? arguments[1] : undefined;
  var condition = /^.+(\s&\s|\+|\/|\sand\s).+$/;
  var message;
  if (value && condition.test(value)) {
    message = options.message || 'Please use only one name';
  }
  return message;
};

/**
 * Returns error if not valid telephone format.
 *  Currently passes validation for the following formats:
 // 0000000000 and 000-000-0000
 // 0000000000x1234 and 000-000-0000x1234
 // 000000.0000x1234 and (000)000.0000x1234 (BPS allowed)
 // 0000000000 x1234 and 000-000-0000 x1234
 // 10000000000 and +10000000000 (country code)
 // 440000000000 and +440000000000 (country code)
 * @param  {STRING} options.message - custom message for error
 */
var validTelephone = function validTelephone() {
  var options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var value = arguments.length > 1 ? arguments[1] : undefined;
  var condition = /^(\+?\d{1,2})?\s*?\(?(\d{3})\)?\s*-?(\d{3})-?[-.]?(\d{4})\s*(x\d{1,4})?$/i;
  var message;
  if (value && !condition.test(value)) {
    message = options.message || 'A valid primary phone number is required, such as :555-555-5555, 555-555-5555 x555';
  }
  return message;
};

/**
 * Returns error if not valid Zip Code format
 * @param  {STRING} options.message - custom message for error
 */
var validZipcode = function validZipcode() {
  var options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var value = arguments.length > 1 ? arguments[1] : undefined;
  var condition = /^\d{5}$/;
  var message;
  if (value && !condition.test(value)) {
    message = options.message || 'A valid Zip Code is required';
  }
  return message;
};

/**
 * Returns error if not valid social security number format
 * @param  {STRING} options.message  - custom message for error
 */
var validSSN = function validSSN() {
  var options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var value = arguments.length > 1 ? arguments[1] : undefined;
  var condition = /^\d{3}-?\d{2}-?\d{4}$/;
  var message;
  if (value && !condition.test(value)) {
    message = options.message || 'Social Security Number entered is not valid';
  }
  return message;
};

/**
 * Returns error if router number does not pass checksum calculation
 * @param  {STRING} options.message - custom message for error
 */
var validABAChecksum = function validABAChecksum() {
  var options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var val = arguments.length > 1 ? arguments[1] : undefined;
  var message;
  var value = String(val);
  var checksum = 0;
  if (/^\d{9}$/.test(value)) {
    for (var i = 0; i < value.length; i += 3) {
      checksum += Number(value.charAt(i)) * 3;
      checksum += Number(value.charAt(i + 1)) * 7;
      checksum += Number(value.charAt(i + 2)) * 1;
    }
  }
  if (value && (checksum === 0 || checksum % 10 !== 0)) {
    message = options.message || 'We cannot identify your routing number. Please enter it again; make sure it includes 9 digits without any dashes, spaces or letters.';
  }
  return message;
};

/**
 * Returns error if not valid bank account number format
 * @param  {STRING} options.message  - custom message for error
 */
var validBankAccount = function validBankAccount() {
  var options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var value = arguments.length > 1 ? arguments[1] : undefined;
  var condition = /^\d+$/;
  var message;
  if (value && !condition.test(value)) {
    message = options.message || 'Please enter your account number using only numbers, without any dashes, spaces or letters';
  }
  return message;
};

/**
 * Returns error if field does not match the set of values
 * @param {STRING[]|NUMBER[]} options.whitelist - List of values that are accepted
 * @param {STRING} options.message - custom message for error
 */
var whitelist = function whitelist() {
  var options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var value = arguments.length > 1 ? arguments[1] : undefined;
  var wlist = options.whitelist || [];
  var message;
  if (wlist.indexOf(value) === -1) {
    message = options.message || 'Please enter a valid value.';
  }
  return message;
};
module.exports = {
  required: required,
  isNumber: isNumber,
  isDollarValue: isDollarValue,
  range: range,
  dollarAmountRange: dollarAmountRange,
  inputLength: inputLength,
  customCondition: customCondition,
  suspicious: suspicious,
  email: email,
  sensitiveInfo: sensitiveInfo,
  sameAsField: sameAsField,
  notSameAsField: notSameAsField,
  validPassword: validPassword,
  twoNames: twoNames,
  validTelephone: validTelephone,
  validZipcode: validZipcode,
  validSSN: validSSN,
  validABAChecksum: validABAChecksum,
  validBankAccount: validBankAccount,
  whitelist: whitelist
};
